import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { RevealItem, revealContainer } from '../common/Reveal';
import { format } from 'date-fns';
import type { ViewTab } from '../layout/NavTabs';
import { RangeSelect } from '../common/RangeSelect';
import { CallVolumeChart } from '../reports/CallVolumeChart';
import { HomeHero } from '../home/HomeHero';
import { HomeKpis } from '../home/HomeKpis';
import { TopPerformers } from '../home/TopPerformers';
import { TeamPerformance } from '../home/TeamPerformance';
import { CallMix } from '../home/CallMix';
import { NeedsAttention } from '../home/NeedsAttention';
import { InsightsDialog } from '../home/InsightsDialog';
import { SourceTabs } from '../home/SourceTabs';
import { BlandOverview } from '../bland/BlandOverview';
import { sumSeries } from '../../utils/series';
import { blandChartTitle, previousLabels, scopeLabels } from '../../utils/range';
import type { DateRange } from '../../types/voip';
import type { CallsInsightHubState } from '../CallsInsightHub/useCallsInsightHub';
const comparedTo: Record<DateRange, string> = {
  today: 'yesterday',
  '7d': 'last week',
  '30d': 'last month'
};
interface HomeViewProps {
  hub: CallsInsightHubState;
  onNavigate: (tab: ViewTab) => void;
}
export function HomeView({
  hub,
  onNavigate
}: HomeViewProps) {
  const [lastUpdated, setLastUpdated] = useState('4:58 PM');
  const [refreshing, setRefreshing] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const scope = scopeLabels[hub.range];
  const teamSeries = useMemo(() => sumSeries(hub.rankedTeamRows.map(r => r.series)), [hub.rankedTeamRows]);
  const delta = hub.kpiTrend.totalCalls;
  const insight = `Call volume is ${delta >= 0 ? 'up' : 'down'} ${Math.abs(delta)}% compared to ${comparedTo[hub.range]}.`;
  const refresh = () => {
    setRefreshing(true);
    window.setTimeout(() => {
      setLastUpdated(format(new Date(), 'h:mm a'));
      setRefreshing(false);
    }, 450);
  };
  return <>
      <RevealItem data-id="element-756" data-mp="3j442lm5rbw1m">
        <HomeHero lastUpdated={lastUpdated} refreshing={refreshing} onRefresh={refresh} insight={insight} onOpenInsights={() => setInsightsOpen(true)} data-id="element-757" data-mp="3j3lvfuhk3xex" />
      </RevealItem>

      <RevealItem className="mb-6" data-id="element-758" data-mp="3e5srz97n1hic">
        <SourceTabs voiceTotals={hub.voiceTotals} bland={hub.bland.funnel} value={hub.source} onChange={hub.setSource} data-id="element-759" data-mp="3e6vfd75r51ti" />
      </RevealItem>

      {hub.source === 'bland' ? <BlandOverview report={hub.bland} scopeLabel={scope} chartTitle={blandChartTitle(hub.range)} data-id="element-760" data-mp="aokbasoncnp7" /> : <>
          <HomeKpis totals={hub.totals} trend={hub.kpiTrend} volume={hub.volume} teamSeries={teamSeries} previousLabel={previousLabels[hub.range]} onOpen={() => onNavigate('reports')} data-id="element-761" data-mp="3suyo2ob6irm0" />

          <motion.div variants={revealContainer} className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]" data-id="element-762" data-mp="2lipvemmivguq">
            <RevealItem fill data-id="element-763" data-mp="2dwkp98dwmzit">
              <CallVolumeChart data={hub.volume} title="Call volume" subtitle={`Total calls ${hub.range === 'today' ? 'per hour' : 'per day'}, ${scope}`} action={<RangeSelect value={hub.range} onChange={hub.setRange} data-id="element-765" data-mp="2ix8m4aoteaky" />} data-id="element-764" data-mp="2dzys0hqhdaqs" />
            </RevealItem>
            <RevealItem fill data-id="element-766" data-mp="1v74l8b30goxn">
              <TopPerformers rows={hub.rankedTeamRows} scopeLabel={scope} onSelect={hub.setSelectedId} onViewAll={() => onNavigate('leaderboard')} data-id="element-767" data-mp="1v8fqygg0o6re" />
            </RevealItem>
          </motion.div>

          <motion.div variants={revealContainer} className="mt-6 grid gap-6 lg:grid-cols-3" data-id="element-768" data-mp="2067jncohjwwn">
            <RevealItem fill data-id="element-769" data-mp="1cgmugc1qo3ex">
              <TeamPerformance summaries={hub.teamSummaries} onViewAll={() => onNavigate('reports')} data-id="element-770" data-mp="1cfv9dv5rpo2w" />
            </RevealItem>
            <RevealItem fill data-id="element-771" data-mp="17fz5dvx2lwnc">
              <CallMix totals={hub.totals} onViewAll={() => onNavigate('reports')} data-id="element-772" data-mp="aqyfn01xksfr" />
            </RevealItem>
            <RevealItem fill data-id="element-773" data-mp="ers3jtfdi78n">
              <NeedsAttention rows={hub.floor.attention} subtitle="Sorted by severity" onSelect={hub.setSelectedId} onViewAll={() => onNavigate('users')} data-id="element-774" data-mp="et331h1scsty" />
            </RevealItem>
          </motion.div>
        </>}

      <AnimatePresence data-id="element-775" data-mp="tgegxplfwox8">
        {insightsOpen && <InsightsDialog hub={hub} onClose={() => setInsightsOpen(false)} data-id="element-776" data-mp="yg97fs1azchr" />}
      </AnimatePresence>
    </>;
}